import { useParams } from 'react-router';
import { usePermissions } from '@/entities/user';
import { useKnowledgeBaseFilters, useKnowledgeBasesList } from '@/features/knowledge-bases';
import { useKnowledgeBaseModal } from '@/features/knowledge-bases/model/knowledge-base-modal.store';
import { ROUTES } from '@/shared/config/routes';
import type { UseKnowledgeFilesWorkspaceReturn } from './useKnowledgeFilesWorkspace.types';

/**
 * Collection list, visibility filter, and create action for the workspace
 * shell. Instantiated once so filter state is shared between the rail and
 * resets per visit, matching `useKnowledgeBaseFilters`'s existing behavior.
 */
export function useKnowledgeFilesWorkspace(): UseKnowledgeFilesWorkspaceReturn {
  const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();
  const { isOrganizationAdmin, canManageAnyKnowledgeBase } = usePermissions();
  const { data: knowledgeBases, status } = useKnowledgeBasesList(isOrganizationAdmin);
  const { filteredKnowledgeBases, filters, updateFilter } = useKnowledgeBaseFilters(
    knowledgeBases ?? [],
  );
  const { createKnowledgeBaseModal } = useKnowledgeBaseModal();

  return {
    collections: filteredKnowledgeBases,
    isLoadingCollections: status === 'pending',
    activeCollectionId: knowledgeBaseId ?? null,
    spaceVisibility: filters.spaceVisibility,
    onSpaceVisibilityChange: (visibility) => updateFilter('spaceVisibility', visibility),
    canCreate: canManageAnyKnowledgeBase,
    onCreate: createKnowledgeBaseModal,
    hrefFor: (collectionId) => ROUTES.KNOWLEDGE_HUB_KB_DETAIL(collectionId),
  };
}
